/**
 * Rounded Elbow Path Strategy
 *
 * Creates right-angle (elbow) paths with rounded corners between elements.
 * Routes: source → horizontal/vertical → corner → horizontal/vertical → target.
 * Corners use quadratic bezier arcs for smooth transitions.
 */

import type { Bound } from '@blocksuite/affine/global/gfx';

import type { AnchorPosition, EdgePathType, Point } from '../../types';
import { BasePathStrategy } from './base-path-strategy';
import type { IEdgePathStrategy, PathRoutingContext } from './types';

/** Corner radius for rounded elbows (px) */
const CORNER_RADIUS = 8;

/**
 * Rounded elbow path strategy.
 *
 * Creates L-shaped or Z-shaped paths with rounded corners.
 * Exit direction is determined by which side the anchor is on.
 */
export class RoundedElbowPathStrategy
  extends BasePathStrategy
  implements IEdgePathStrategy
{
  readonly type: EdgePathType = 'rounded-elbow';

  calculatePath(
    source: Bound,
    target: Bound,
    sourceAnchorHint?: AnchorPosition,
    targetAnchorHint?: AnchorPosition,
    routingContext?: PathRoutingContext
  ): string {
    const { sourceAnchor: s, targetAnchor: t } = this.findAnchors(
      source,
      target,
      sourceAnchorHint,
      targetAnchorHint
    );

    const sSide =
      sourceAnchorHint && sourceAnchorHint !== 'auto'
        ? sourceAnchorHint
        : this.getAnchorSide(source, s);
    const tSide =
      targetAnchorHint && targetAnchorHint !== 'auto'
        ? targetAnchorHint
        : this.getAnchorSide(target, t);

    const waypoints = this.computeElbowWaypoints(
      s,
      t,
      sSide,
      tSide,
      routingContext?.sourceObstacle ?? source,
      routingContext?.targetObstacle ?? target
    );
    return this.buildRoundedPath(s, waypoints, t);
  }

  getPathPoints(source: Bound, target: Bound, segments = 20): Point[] {
    const { sourceAnchor: s, targetAnchor: t } = this.findNearestAnchors(
      source,
      target
    );

    const sSide = this.getAnchorSide(source, s);
    const tSide = this.getAnchorSide(target, t);
    const waypoints = this.computeElbowWaypoints(
      s,
      t,
      sSide,
      tSide,
      source,
      target
    );
    return this.samplePolyline([s, ...waypoints, t], segments);
  }

  calculateSelfLoopPath(bound: Bound): string {
    return this.buildSelfLoopPath(bound);
  }

  getSelfLoopPathPoints(bound: Bound, segments = 20): Point[] {
    return this.buildSelfLoopPathPoints(bound, segments);
  }

  /**
   * Builds an SVG path with rounded corners at each waypoint.
   * Uses quadratic bezier arcs (Q command) for smooth corners.
   */
  private buildRoundedPath(
    start: Point,
    waypoints: Point[],
    end: Point
  ): string {
    const distinctPoints = [start, ...waypoints, end].filter(
      (point, index, points) =>
        index === 0 || this.distance(points[index - 1], point) > 0.000001
    );
    const allPoints = distinctPoints.filter((point, index, points) => {
      if (index === 0 || index === points.length - 1) return true;
      const previous = points[index - 1];
      const next = points[index + 1];
      const crossProduct =
        (point.x - previous.x) * (next.y - point.y) -
        (point.y - previous.y) * (next.x - point.x);
      return Math.abs(crossProduct) > 0.000001;
    });

    if (allPoints.length <= 2) {
      return `M ${start.x} ${start.y} L ${end.x} ${end.y}`;
    }

    let path = `M ${start.x} ${start.y}`;

    for (let i = 1; i < allPoints.length - 1; i++) {
      const prev = allPoints[i - 1];
      const curr = allPoints[i];
      const next = allPoints[i + 1];

      // Compute approach and departure distances
      const dIn = this.distance(prev, curr);
      const dOut = this.distance(curr, next);
      const r = Math.min(CORNER_RADIUS, dIn / 2, dOut / 2);

      // Points where the arc starts and ends (offset from corner)
      const arcStart = this.interpolate(curr, prev, r / dIn);
      const arcEnd = this.interpolate(curr, next, r / dOut);

      // Line to arc start, then quadratic bezier through corner to arc end
      path += ` L ${arcStart.x} ${arcStart.y}`;
      path += ` Q ${curr.x} ${curr.y} ${arcEnd.x} ${arcEnd.y}`;
    }

    path += ` L ${end.x} ${end.y}`;
    return path;
  }

  /** Interpolates from `from` towards `to` by a fixed distance ratio. */
  private interpolate(from: Point, to: Point, ratio: number): Point {
    return {
      x: from.x + (to.x - from.x) * ratio,
      y: from.y + (to.y - from.y) * ratio,
    };
  }
}
